import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { PhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';
import { SiteNav, MinimalFooter } from './shared';
import Seo from './Seo';
import { buildDashboardApiUrl } from '../dashboard/apiConfig';

/* -------------------------------------------------------------------------
   Mieter-Selbstauskunft — public tenant self-disclosure form.
   Editorial (homev2) look & feel. Submits to the FastAPI backend, which
   emails the filled form to vermietung@hambacore.com via Microsoft Graph.
   ------------------------------------------------------------------------- */

const SUBMIT_URL = buildDashboardApiUrl('/api/mieterselbstauskunft/submit');

const EMPTY_APPLICANT = {
  name: '', familienstand: '', geburtsdatum: '', anschrift: '', telefon: '',
  email: '', beruf: '', nettoeinkommen: '', arbeitgeber: '', vermieter: '',
  mietrueckstaende: null, raeumungsklage: null, zwangsvollstreckung: null,
  eidesstattliche_versicherung: null, insolvenzverfahren: null, vorstrafen: null,
  sozialleistungen: null, gewerbliche_nutzung: null, tierhaltung: null,
  anmerkungen: '',
};

// Selbstauskunft yes/no questions — label + state key. Mirrors the PDF.
const YESNO = [
  ['Bestehen Mietrückstände aus bisherigen Mietverhältnissen?', 'mietrueckstaende'],
  ['Wurde in den letzten 5 Jahren Räumungsklage gegen Sie erhoben?', 'raeumungsklage'],
  ['Wurde in den letzten 5 Jahren Zwangsvollstreckung gegen Sie eingeleitet?', 'zwangsvollstreckung'],
  ['Haben Sie in den letzten 5 Jahren eine eidesstattliche Versicherung abgegeben?', 'eidesstattliche_versicherung'],
  ['Wurde in den letzten 5 Jahren ein Insolvenzverfahren gegen Sie eröffnet?', 'insolvenzverfahren'],
  ['Vorstrafen / Haftbefehl (im Zusammenhang mit Mietverhältnissen)?', 'vorstrafen'],
  ['Beziehen Sie Sozialleistungen zur Zahlung der Miete / Kaution?', 'sozialleistungen'],
  ['Ist eine gewerbliche Nutzung der Wohnung beabsichtigt?', 'gewerbliche_nutzung'],
  ['Ist Tierhaltung beabsichtigt?', 'tierhaltung'],
];

const EMAIL_RE = /^.+@.+\..+$/;

// Format free-typed digits into DD.MM.YYYY as the user types — dots are
// inserted automatically, so the user only enters numbers. Deleting works
// because we always re-derive from the raw digits.
function formatDate(input) {
  const d = (input || '').replace(/\D/g, '').slice(0, 8); // DDMMYYYY
  const parts = [d.slice(0, 2)];
  if (d.length >= 3) parts.push(d.slice(2, 4));
  if (d.length >= 5) parts.push(d.slice(4, 8));
  return parts.filter((p) => p !== '').join('.');
}

// --- Field primitives (homev2 .field styling) ----------------------------

function TextField({ label, value, onChange, type = 'text', required, error, placeholder, autoComplete = 'off' }) {
  return (
    <div className={`field ${error ? 'error' : ''}`}>
      <label className="field-label">
        {label}{required && <span style={{ color: 'var(--bordeaux)', marginLeft: 4 }}>*</span>}
      </label>
      <input
        type={type}
        value={value}
        placeholder={placeholder}
        autoComplete={autoComplete}
        onChange={(e) => onChange(e.target.value)}
      />
      {error && <div className="field-error">{error}</div>}
    </div>
  );
}

function DateField({ label, value, onChange, required, error }) {
  return (
    <div className={`field ${error ? 'error' : ''}`}>
      <label className="field-label">
        {label}{required && <span style={{ color: 'var(--bordeaux)', marginLeft: 4 }}>*</span>}
      </label>
      <input
        type="text"
        inputMode="numeric"
        placeholder="TT.MM.JJJJ"
        value={value}
        autoComplete="off"
        onChange={(e) => onChange(formatDate(e.target.value))}
      />
      {error && <div className="field-error">{error}</div>}
    </div>
  );
}

function PhoneField({ label, value, onChange, required, error }) {
  return (
    <div className={`field phone-field ${error ? 'error' : ''}`}>
      <label className="field-label">
        {label}{required && <span style={{ color: 'var(--bordeaux)', marginLeft: 4 }}>*</span>}
      </label>
      <PhoneInput
        defaultCountry="de"
        value={value}
        onChange={(phone) => onChange(phone)}
        inputProps={{ 'aria-label': label, autoComplete: 'tel' }}
      />
      {error && <div className="field-error">{error}</div>}
    </div>
  );
}

// Thousands-separated numeric input (e.g. 3.200), matching the funnel's NKM
// field. State holds raw digits; the display is formatted de-DE.
function NumberField({ label, value, onChange, suffix, required, error }) {
  const formatted = value ? Number(value).toLocaleString('de-DE') : '';
  return (
    <div className={`field number-field ${error ? 'error' : ''}`}>
      <label className="field-label">
        {label}{required && <span style={{ color: 'var(--bordeaux)', marginLeft: 4 }}>*</span>}
      </label>
      <div className="number-wrap">
        <input
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          value={formatted}
          autoComplete="off"
          aria-label={label}
          onChange={(e) => onChange(e.target.value.replace(/\D/g, ''))}
        />
        {suffix && <span className="number-suffix">{suffix}</span>}
      </div>
      {error && <div className="field-error">{error}</div>}
    </div>
  );
}

function TextArea({ label, value, onChange, rows = 3, hint }) {
  return (
    <div className="field">
      <label className="field-label">{label}</label>
      <textarea value={value} rows={rows} onChange={(e) => onChange(e.target.value)} style={{ resize: 'vertical' }} />
      {hint && <div className="field-hint">{hint}</div>}
    </div>
  );
}

function YesNo({ label, value, onChange }) {
  const opt = (val, text) => {
    const selected = value === val;
    return (
      <button
        type="button"
        onClick={() => onChange(val)}
        style={{
          padding: '8px 20px',
          minWidth: 76,
          background: selected ? 'var(--bordeaux)' : 'var(--cream)',
          color: selected ? 'var(--cream)' : 'var(--anthracite)',
          border: `1px solid ${selected ? 'var(--bordeaux)' : 'var(--cream-line)'}`,
          fontFamily: 'var(--grotesk)',
          fontSize: 13.5,
          borderRadius: 1,
          cursor: 'pointer',
          transition: 'background 160ms ease, border-color 160ms ease',
        }}
      >
        {text}
      </button>
    );
  };
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', padding: '10px 0', borderBottom: '1px solid var(--cream-line)' }}>
      <span style={{ flex: '1 1 320px', fontSize: 14.5, color: 'var(--anthracite)', lineHeight: 1.45 }}>{label}</span>
      <div style={{ display: 'flex', gap: 8 }}>
        {opt(false, 'Nein')}
        {opt(true, 'Ja')}
      </div>
    </div>
  );
}

function Consent({ checked, onToggle, error, children }) {
  return (
    <div>
      <button
        type="button"
        onClick={() => onToggle(!checked)}
        style={{
          display: 'flex', alignItems: 'flex-start', gap: 12, width: '100%', textAlign: 'left',
          background: 'transparent', border: 'none', padding: '8px 0', cursor: 'pointer',
        }}
      >
        <span
          aria-hidden="true"
          style={{
            marginTop: 2, flexShrink: 0, width: 18, height: 18,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            border: `1px solid ${checked ? 'var(--bordeaux)' : (error ? 'var(--bordeaux)' : 'var(--charcoal-faint)')}`,
            background: checked ? 'var(--bordeaux)' : 'transparent',
          }}
        >
          {checked && (
            <svg width="11" height="11" viewBox="0 0 12 12">
              <path d="M2 6.5 L5 9 L10 3" stroke="var(--cream)" strokeWidth="1.9" fill="none" strokeLinecap="square" />
            </svg>
          )}
        </span>
        <span style={{ fontSize: 13.5, lineHeight: 1.55, color: 'var(--charcoal-muted)', fontFamily: 'var(--grotesk)' }}>
          {children}
        </span>
      </button>
    </div>
  );
}

function SectionTitle({ children, sub }) {
  return (
    <div style={{ margin: '44px 0 20px' }}>
      <h2 style={{ fontFamily: 'var(--serif)', fontSize: 'clamp(22px, 2.4vw, 28px)', color: 'var(--anthracite)', margin: 0, lineHeight: 1.2 }}>
        {children}
      </h2>
      {sub && <p className="muted" style={{ fontSize: 13.5, marginTop: 6, maxWidth: '60ch' }}>{sub}</p>}
    </div>
  );
}

function Grid2({ children }) {
  return <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', columnGap: 28, rowGap: 24 }}>{children}</div>;
}

// --- Applicant sub-form ---------------------------------------------------

function ApplicantFields({ data, set, errors = {} }) {
  return (
    <>
      <Grid2>
        <TextField label="Name, Vorname (ggf. Geburtsname)" required value={data.name} onChange={(v) => set('name', v)}
          error={errors.name} autoComplete="name" />
        <TextField label="Familienstand" value={data.familienstand} onChange={(v) => set('familienstand', v)} />
        <DateField label="Geburtsdatum" value={data.geburtsdatum} onChange={(v) => set('geburtsdatum', v)} />
        <PhoneField label="Telefon / Mobilfunk" value={data.telefon} onChange={(v) => set('telefon', v)} />
        <TextField label="E-Mail-Adresse" required type="email" value={data.email} onChange={(v) => set('email', v)}
          error={errors.email} autoComplete="email" />
        <TextField label="Ausgeübter Beruf" value={data.beruf} onChange={(v) => set('beruf', v)} />
        <NumberField label="Mtl. Nettoeinkommen" suffix="€" value={data.nettoeinkommen} onChange={(v) => set('nettoeinkommen', v)} />
      </Grid2>
      <div style={{ marginTop: 24 }}>
        <TextArea label="Aktuelle Anschrift (Straße | PLZ | Ort)" value={data.anschrift} onChange={(v) => set('anschrift', v)} rows={2} />
      </div>
      <div style={{ marginTop: 24 }}>
        <TextArea label="Derzeitiger Arbeitgeber (Name | Anschrift | Telefon | seit wann beschäftigt?)" value={data.arbeitgeber} onChange={(v) => set('arbeitgeber', v)} rows={2} />
      </div>
      <div style={{ marginTop: 24 }}>
        <TextArea label="Derzeitiger Vermieter (Name | Anschrift | Telefon)" value={data.vermieter} onChange={(v) => set('vermieter', v)} rows={2} />
      </div>

      <div style={{ marginTop: 28 }}>
        {YESNO.map(([label, key]) => (
          <YesNo key={key} label={label} value={data[key]} onChange={(v) => set(key, v)} />
        ))}
      </div>
      <div style={{ marginTop: 24 }}>
        <TextArea label="Ergänzende Anmerkungen (z. B. „falls ja, wann / welche …“)" value={data.anmerkungen} onChange={(v) => set('anmerkungen', v)} rows={2} />
      </div>
    </>
  );
}

// --- Page -----------------------------------------------------------------

export default function MieterselbstauskunftPage() {

  const [objekt, setObjekt] = useState('');
  const [mietbeginn, setMietbeginn] = useState('');

  const [a1, setA1] = useState({ ...EMPTY_APPLICANT });
  const [hasA2, setHasA2] = useState(false);
  const [a2, setA2] = useState({ ...EMPTY_APPLICANT });

  const [weitere, setWeitere] = useState([]); // {name_anschrift, geburtsdatum, verwandtschaft}

  const [consentAgb, setConsentAgb] = useState(false);
  const [consentDs, setConsentDs] = useState(false);
  const [consentRichtig, setConsentRichtig] = useState(false);

  const [website, setWebsite] = useState(''); // honeypot

  const [showErrors, setShowErrors] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [done, setDone] = useState(false);

  const setA1Field = (k, v) => setA1((d) => ({ ...d, [k]: v }));
  const setA2Field = (k, v) => setA2((d) => ({ ...d, [k]: v }));

  const consentsOk = consentAgb && consentDs && consentRichtig;

  // Name + E-Mail are mandatory for the 1st applicant, and for the 2nd when added.
  const emailBad = (v) => !v.trim() || !EMAIL_RE.test(v.trim());
  const a1Errors = {
    name: showErrors && !a1.name.trim() ? 'Bitte ausfüllen.' : '',
    email: showErrors && emailBad(a1.email) ? 'Gültige E-Mail erforderlich.' : '',
  };
  const a2Errors = {
    name: showErrors && hasA2 && !a2.name.trim() ? 'Bitte ausfüllen.' : '',
    email: showErrors && hasA2 && emailBad(a2.email) ? 'Gültige E-Mail erforderlich.' : '',
  };
  const formValid =
    a1.name.trim() && !emailBad(a1.email) &&
    (!hasA2 || (a2.name.trim() && !emailBad(a2.email))) &&
    consentsOk;

  const addPerson = () => setWeitere((w) => (w.length >= 5 ? w : [...w, { name_anschrift: '', geburtsdatum: '', verwandtschaft: '' }]));
  const setPerson = (i, k, v) => setWeitere((w) => w.map((p, idx) => (idx === i ? { ...p, [k]: v } : p)));
  const removePerson = (i) => setWeitere((w) => w.filter((_, idx) => idx !== i));

  const handleSubmit = async () => {
    if (submitting) return;
    if (!formValid) {
      setShowErrors(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    setSubmitting(true);
    setSubmitError(null);

    // Send Nettoeinkommen as a readable "3.200 €" string in the email.
    const withEuro = (a) => ({
      ...a,
      nettoeinkommen: a.nettoeinkommen ? `${Number(a.nettoeinkommen).toLocaleString('de-DE')} €` : '',
    });
    const payload = {
      objekt, mietbeginn,
      applicant1: withEuro(a1),
      applicant2: hasA2 ? withEuro(a2) : null,
      weitere_personen: weitere.filter((p) => p.name_anschrift.trim() || p.geburtsdatum.trim() || p.verwandtschaft.trim()),
      consent_agb: consentAgb,
      consent_datenschutz: consentDs,
      consent_richtigkeit: consentRichtig,
      website,
    };

    try {
      const res = await fetch(SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        let msg = 'Ihre Auskunft konnte nicht übermittelt werden. Bitte versuchen Sie es später erneut.';
        try {
          const body = await res.json();
          if (body && body.detail) msg = body.detail;
        } catch { /* ignore parse errors */ }
        throw new Error(msg);
      }
      setDone(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setSubmitError(err.message || 'Ihre Auskunft konnte nicht übermittelt werden. Bitte versuchen Sie es später erneut.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="page-enter" data-screen-label="Mieterselbstauskunft">
      <Seo
        path="/mieterselbstauskunft"
        title="Mieter-Selbstauskunft — hamba"
        description="Formular zur freiwilligen Mieter-Selbstauskunft der hamba GmbH."
        noindex
      />
      <SiteNav />

      <main style={{ maxWidth: 820, margin: '0 auto', padding: '96px var(--gutter) 40px' }}>
        {done ? (
          <div style={{ paddingTop: 24 }}>
            <div className="eyebrow" style={{ color: 'var(--bordeaux)', marginBottom: 14 }}>Vielen Dank</div>
            <h1 style={{ fontFamily: 'var(--serif)', fontSize: 'clamp(30px, 4vw, 44px)', color: 'var(--anthracite)', margin: '0 0 16px', lineHeight: 1.15 }}>
              Ihre Selbstauskunft ist bei uns eingegangen.
            </h1>
            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--charcoal-muted)', maxWidth: '60ch' }}>
              Wir prüfen Ihre Angaben und melden uns zeitnah bei Ihnen. Bei Rückfragen erreichen Sie uns unter{' '}
              <a href="mailto:vermietung@hambacore.com" style={{ color: 'var(--bordeaux)' }}>vermietung@hambacore.com</a>.
            </p>
            <Link to="/" style={{ display: 'inline-block', marginTop: 28, color: 'var(--bordeaux)', fontSize: 14 }}>← Zurück zur Startseite</Link>
          </div>
        ) : (
          <>
            <div className="eyebrow" style={{ color: 'var(--charcoal-muted)', marginBottom: 14 }}>Formular · vertraulich</div>
            <h1 style={{ fontFamily: 'var(--serif)', fontSize: 'clamp(30px, 4vw, 46px)', color: 'var(--anthracite)', margin: '0 0 16px', lineHeight: 1.12 }}>
              Mieter-Selbstauskunft
            </h1>
            <p style={{ fontSize: 15.5, lineHeight: 1.7, color: 'var(--charcoal-muted)', maxWidth: '66ch' }}>
              Die Selbstauskunft ist freiwillig. Ihre Entscheidung für eine mögliche Vermietung stützt der Vermieter
              jedoch auf vollständige und wahrheitsgemäße Angaben. Ihre Daten werden ausschließlich zum Zwecke der
              Vermietung verwendet und nach Bundesdatenschutzgesetz behandelt.
            </p>

            {showErrors && !formValid && (
              <p role="alert" style={{ marginTop: 20, padding: '12px 16px', border: '1px solid var(--bordeaux)', background: 'rgba(90,42,42,0.05)', color: 'var(--bordeaux)', fontSize: 14 }}>
                Bitte füllen Sie die Pflichtfelder (Name, E-Mail) aus und bestätigen Sie die Einwilligungen am Ende des Formulars.
              </p>
            )}

            {/* Objekt */}
            <SectionTitle sub="Für welches Objekt interessieren Sie sich?">Mietobjekt</SectionTitle>
            <TextArea label="Adresse / Lage des Mietobjekts" value={objekt} onChange={setObjekt} rows={2} />
            <div style={{ marginTop: 24 }}>
              <Grid2>
                <DateField label="Anmietung ab dem" value={mietbeginn} onChange={setMietbeginn} />
              </Grid2>
            </div>

            {/* Applicant 1 */}
            <SectionTitle>1. Mietinteressent/in</SectionTitle>
            <ApplicantFields data={a1} set={setA1Field} errors={a1Errors} />

            {/* Applicant 2 (optional) */}
            {!hasA2 ? (
              <button
                type="button"
                onClick={() => setHasA2(true)}
                style={{ marginTop: 36, padding: '11px 20px', background: 'transparent', color: 'var(--anthracite)', border: '1px solid var(--anthracite)', fontFamily: 'var(--grotesk)', fontSize: 13, letterSpacing: '0.04em', borderRadius: 1, cursor: 'pointer' }}
              >
                + 2. Mietinteressent/in hinzufügen
              </button>
            ) : (
              <>
                <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
                  <SectionTitle>2. Mietinteressent/in</SectionTitle>
                  <button type="button" onClick={() => setHasA2(false)} style={{ background: 'none', border: 'none', color: 'var(--bordeaux)', fontSize: 13, cursor: 'pointer' }}>
                    Entfernen
                  </button>
                </div>
                <ApplicantFields data={a2} set={setA2Field} errors={a2Errors} />
              </>
            )}

            {/* Weitere Personen */}
            <SectionTitle sub="Außer Ihnen sollen weitere Personen die Wohnung beziehen?">Weitere einziehende Personen</SectionTitle>
            {weitere.map((p, i) => (
              <div key={i} style={{ marginBottom: 20 }}>
                <Grid2>
                  <TextField label="Name, Anschrift" value={p.name_anschrift} onChange={(v) => setPerson(i, 'name_anschrift', v)} />
                  <DateField label="Geburtsdatum" value={p.geburtsdatum} onChange={(v) => setPerson(i, 'geburtsdatum', v)} />
                  <TextField label="Verwandtschaftsverhältnis" value={p.verwandtschaft} onChange={(v) => setPerson(i, 'verwandtschaft', v)} />
                </Grid2>
                <button type="button" onClick={() => removePerson(i)} style={{ background: 'none', border: 'none', color: 'var(--bordeaux)', fontSize: 12.5, cursor: 'pointer', marginTop: 8, padding: 0 }}>
                  Zeile entfernen
                </button>
              </div>
            ))}
            {weitere.length < 5 && (
              <button type="button" onClick={addPerson} style={{ padding: '10px 18px', background: 'transparent', color: 'var(--anthracite)', border: '1px solid var(--cream-line)', fontFamily: 'var(--grotesk)', fontSize: 13, borderRadius: 1, cursor: 'pointer' }}>
                + Person hinzufügen
              </button>
            )}

            {/* Consents */}
            <SectionTitle>Einwilligungen</SectionTitle>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <Consent checked={consentRichtig} onToggle={setConsentRichtig} error={showErrors && !consentRichtig}>
                Ich erkläre, dass ich in der Lage bin, alle Verpflichtungen aus dem Mietvertrag (insb. Kaution, Miete
                nebst Nebenkosten) zu leisten, und dass die vorstehenden Angaben vollständig und wahrheitsgemäß sind.
                <span style={{ color: 'var(--bordeaux)' }}> *</span>
              </Consent>
              <Consent checked={consentAgb} onToggle={setConsentAgb} error={showErrors && !consentAgb}>
                Ich habe die{' '}
                <Link to="/agb" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--bordeaux)' }}>AGB</Link>
                {' '}gelesen und akzeptiere sie.
                <span style={{ color: 'var(--bordeaux)' }}> *</span>
              </Consent>
              <Consent checked={consentDs} onToggle={setConsentDs} error={showErrors && !consentDs}>
                Ich habe die{' '}
                <Link to="/datenschutz" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--bordeaux)' }}>Datenschutzhinweise</Link>
                {' '}zur Kenntnis genommen.
                <span style={{ color: 'var(--bordeaux)' }}> *</span>
              </Consent>
            </div>

            {/* Honeypot — visually hidden, not announced to AT */}
            <div aria-hidden="true" style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, overflow: 'hidden' }}>
              <label>Website<input tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)} /></label>
            </div>

            {submitError && (
              <p role="alert" style={{ marginTop: 24, padding: '14px 18px', border: '1px solid var(--bordeaux)', background: 'rgba(90,42,42,0.05)', color: 'var(--bordeaux)', fontSize: 14, lineHeight: 1.55 }}>
                {submitError}
              </p>
            )}

            <div style={{ marginTop: 36, display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={submitting}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 10, padding: '14px 30px',
                  background: 'var(--bordeaux)', color: 'var(--cream)', fontFamily: 'var(--grotesk)',
                  fontSize: 13, letterSpacing: '0.08em', fontWeight: 500, border: '1px solid var(--bordeaux)',
                  borderRadius: 1, cursor: submitting ? 'wait' : 'pointer', opacity: submitting ? 0.7 : 1,
                }}
              >
                {submitting ? 'Wird gesendet…' : 'Selbstauskunft absenden'}
                {!submitting && <span aria-hidden="true">→</span>}
              </button>
              <span className="muted" style={{ fontSize: 12 }}><span style={{ color: 'var(--bordeaux)' }}>*</span> Pflichtangabe</span>
            </div>
          </>
        )}
      </main>

      <MinimalFooter />
    </div>
  );
}
